import React from 'react';
import Card from '../components/UI/Card';
import CodeSnippet from '../components/CodeSnippet';
import { API_ENDPOINTS, API_LIMITS } from '../utils/constants'; // removed USER_ROLES

const Docs = () => {
  const authExample = `// Отримання токену
POST /auth/token
Content-Type: application/json

{
  "client_id": "your@email.com",
  "client_secret": "your_password"
}

// Відповідь
{
  "access_token": "eyJhbGciOiJSUzI1NiIs...",
  "expires_in": 1800,
  "token_type": "Bearer",
  "refresh_token": "eyJhbGciOiJSUzI1NiIs..."
}`;

  const paginationExample = `GET /votings?limit=20&cursor=eyJpZCI6MTIzfQ
&date_from=2024-01-01&date_to=2024-12-31&result=adopted`;

  return (
    <div className="py-12">
      <div className="container-custom">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4">Документація API</h1>
          <p className="text-xl text-gray-600">
            REST API для доступу до структурованих даних про голосування органів влади
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card>
                <div className="p-4 space-y-2">
                  <a href="#auth" className="block text-gray-700 hover:text-primary-600 py-2">Автентифікація</a>
                  <a href="#roles" className="block text-gray-700 hover:text-primary-600 py-2">Ролі</a>
                  <a href="#endpoints" className="block text-gray-700 hover:text-primary-600 py-2">Ендпоїнти</a>
                  <a href="#pagination" className="block text-gray-700 hover:text-primary-600 py-2">Пагінація</a>
                  <a href="#filters" className="block text-gray-700 hover:text-primary-600 py-2">Фільтрація</a>
                  <a href="#errors" className="block text-gray-700 hover:text-primary-600 py-2">Помилки</a>
                </div>
              </Card>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-8">

            {/* Authentication */}
            <section id="auth">
              <h2 className="text-2xl font-bold mb-4">Автентифікація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    API використовує JWT токени. Токен діє <strong className="text-primary-600">30 хвилин</strong>.
                    Для отримання токену використовуйте ендпоїнт <code className="bg-gray-100 px-2 py-1 rounded">/auth/token</code>.
                  </p>
                  <CodeSnippet code={authExample} />
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Важливо:</strong> Передавайте токен в заголовку <code className="bg-yellow-100 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</code>
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* New Roles Section */}
            <section id="roles">
              <h2 className="text-2xl font-bold mb-4">Ролі користувачів</h2>
              <p className="text-gray-600 mb-6">
                У системі існує три основні ролі, які визначають рівень доступу до функціоналу платформи.
              </p>
              <div className="space-y-4">
                <Card>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Super‑admin</h3>
                    <p className="text-gray-600 mb-2"><strong>Повний доступ до всіх компонентів.</strong> Має право:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Керувати користувачами (створення, редагування, блокування)</li>
                      <li>Призначати будь‑яку роль (super‑admin, admin)</li>
                      <li>Бачити всіх користувачів (включаючи super‑admin)</li>
                      <li>Доступ до адмін‑панелі (зовнішній сервіс)</li>
                      <li>Керувати ETL‑процесами, джерелами даних</li>
                      <li>Переглядати логи та метрики</li>
                    </ul>
                  </div>
                </Card>
                <Card>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Адміністратор (Admin)</h3>
                    <p className="text-gray-600 mb-2"><strong>Частковий доступ до управління.</strong> Має право:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Керувати користувачами (окрім super‑admin)</li>
                      <li>Призначати роль <code>admin</code> або знімати її</li>
                      <li>Бачити користувачів з роллю <code>admin</code> та звичайних користувачів</li>
                      <li>Не бачить super‑admin та не може їх редагувати</li>
                      <li>Немає доступу до зовнішньої адмін‑панелі</li>
                    </ul>
                  </div>
                </Card>
                <Card>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Аналітик / Інтегратор (Regular user)</h3>
                    <p className="text-gray-600 mb-2"><strong>Тільки API‑доступ.</strong> Має право:</p>
                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                      <li>Отримувати токен та використовувати REST API</li>
                      <li>Доступ до ендпоїнтів з даними про голосування (scope <code>read:data</code>)</li>
                      <li>Переглядати власний профіль та керувати токеном</li>
                      <li>Немає доступу до сторінки управління користувачами</li>
                    </ul>
                  </div>
                </Card>
              </div>
            </section>

            {/* Endpoints */}
            <section id="endpoints">
              <h2 className="text-2xl font-bold mb-4">Ендпоїнти</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Метод</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Шлях</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Опис</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {API_ENDPOINTS.map((endpoint, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <span className="inline-flex px-2 py-1 text-xs font-mono font-semibold bg-primary-100 text-primary-700 rounded">
                              {endpoint.method}
                            </span>
                           </td>
                          <td className="px-6 py-3 font-mono text-sm text-gray-900">{endpoint.path}</td>
                          <td className="px-6 py-3 text-sm text-gray-600">{endpoint.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>

            {/* Pagination */}
            <section id="pagination">
              <h2 className="text-2xl font-bold mb-4">Пагінація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    API використовує cursor-based пагінацію. Максимальний ліміт – <strong className="text-primary-600">{API_LIMITS.paginationLimit}</strong>.
                  </p>
                  <CodeSnippet code={paginationExample} />
                  <div className="mt-4">
                    <p className="font-semibold mb-2">Параметри:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><code className="bg-gray-100 px-2 py-0.5 rounded">limit</code> – кількість записів (≤200)</li>
                      <li><code className="bg-gray-100 px-2 py-0.5 rounded">cursor</code> – курсор для наступної сторінки</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </section>

            {/* Filters */}
            <section id="filters">
              <h2 className="text-2xl font-bold mb-4">Фільтрація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">Більшість ендпоїнтів підтримують фільтрацію:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">organ_id</code><p className="text-xs text-gray-600 mt-1">Фільтр за органом влади</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">season_id</code><p className="text-xs text-gray-600 mt-1">Фільтр за сесією/скликанням</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">date_from / date_to</code><p className="text-xs text-gray-600 mt-1">Фільтр за діапазоном дат</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">fraction_id</code><p className="text-xs text-gray-600 mt-1">Фільтр за фракцією/партією</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">vote_type</code><p className="text-xs text-gray-600 mt-1">yea/nay/abstain/absent/not_voting</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code className="text-sm">keyword</code><p className="text-xs text-gray-600 mt-1">Повнотекстовий пошук</p></div>
                  </div>
                </div>
              </Card>
            </section>

            {/* Error Codes */}
            <section id="errors">
              <h2 className="text-2xl font-bold mb-4">Коди помилок</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Код</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Помилка</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Опис</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr><td className="px-6 py-3 font-mono text-sm">400</td><td className="px-6 py-3">bad_request</td><td className="px-6 py-3 text-sm">Невалідні параметри запиту</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">401</td><td className="px-6 py-3">invalid_token</td><td className="px-6 py-3 text-sm">Відсутній/прострочений токен</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">403</td><td className="px-6 py-3">insufficient_scope</td><td className="px-6 py-3 text-sm">Недостатні права доступу</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">404</td><td className="px-6 py-3">not_found</td><td className="px-6 py-3 text-sm">Сутність відсутня</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">429</td><td className="px-6 py-3">rate_limited</td><td className="px-6 py-3 text-sm">Перевищено ліміт запитів</td></tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Docs;